import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { cartIcon, chevronDownIcon, paperPlaneIcon, qrCodeIcon, questionCircleIcon, searchIcon } from '@progress/kendo-svg-icons';
import { catchError, EMPTY, Observable, of, switchMap, take } from 'rxjs';
import { DashboardCardComponent } from 'src/app/shared/components/dashboard-card/dashboard-card.component';
import { LoadingButtonComponent } from 'src/app/shared/components/loading-button/loading-button.component';
import { LoadingDropdownlistComponent } from 'src/app/shared/components/loading-dropdownlist/loading-dropdownlist/loading-dropdownlist.component';
import { LoadingPanelComponent } from 'src/app/shared/components/loading-panel';
import { QrCodeDialogComponent } from 'src/app/shared/components/qr-code-dialog/qr-code-dialog.component';
import { SelectWarehouseDialogComponent } from 'src/app/shared/components/select-warehouse-dialog/select-warehouse-dialog/select-warehouse-dialog.component';
import { AccountDetailsCardType } from 'src/app/shared/constants/account-details-card-type';
import { ChartSeries } from 'src/app/shared/constants/chart-series-colors';
import { CompanyCode } from 'src/app/shared/constants/company-code';
import { SalesDistrict } from 'src/app/shared/constants/sales-district';
import { CompanyAdminConfiguration } from 'src/app/shared/models/company-admin-configuration';
import { CosmosCustomer } from 'src/app/shared/models/customer';
import { CustomerSettings } from 'src/app/shared/models/customer-settings';
import { DashboardCard, DashboardCardVisibleFor } from 'src/app/shared/models/dashboard-card';
import { Warehouse } from 'src/app/shared/models/warehouse';
import { SecurityService } from 'src/app/shared/security/security.service';
import { isPortalUserAccessDenied } from 'src/app/shared/security/portal-access-error';
import { ICustomerSettingsService } from 'src/app/shared/services/customer-settings/customer-settings.service.interface';
import { ICustomerService } from 'src/app/shared/services/customer/customer.service.interface';
import { KendoAlertService } from 'src/app/shared/services/kendo-alerts.service';
import { IPrintService } from 'src/app/shared/services/print/print.service.interface';
import { WarehouseService } from 'src/app/shared/services/warehouse/warehouse.service';
import { CompanyAdminConfigurationStore } from 'src/app/shared/stores/company-admin-configuration.store';
import { CustomerSettingsStore } from 'src/app/shared/stores/customer-settings.store';
import { AccountDetailCardComponent } from './account-detail-card/account-detail-card.component';

@Component({
  selector: 'ntw-account-detail',
  templateUrl: './account-detail.component.html',
  styleUrls: ['./account-detail.component.scss', '../../shared/styles/button.scss'],
  imports: [
    AccountDetailCardComponent,
    ButtonModule,
    CommonModule,
    DashboardCardComponent,
    LoadingButtonComponent,
    LoadingDropdownlistComponent,
    LoadingPanelComponent,
    QrCodeDialogComponent,
    SelectWarehouseDialogComponent,
    SVGIconModule,
    TranslateModule,
  ],
  animations: [
    trigger('collapse', [
      state('open', style({
        display: '*',
        height: '*',
      })),
      state('closed', style({
        display: 'none',
        height: '0',
      })),
      transition('open => closed', [
        // add overflow clip to prevent content from overflowing during animation
        style({ overflow: 'clip' }),
        // animate the wrapper element height to 0
        animate('0.35s ease', style({ height: '0', paddingTop: '0', paddingBottom: '0' })),
      ]),
      transition('closed => open', [
        // show the wrapper element
        style({ display: '*', overflow: 'clip', paddingTop: '0', paddingBottom: '0' }),
        // animate the wrapper height to its final value
        animate('0.35s ease', style({ height: '*', paddingTop: '*', paddingBottom: '*' })),
      ]),
    ]),
    trigger('rotate', [
      state('collapsed', style({ transform: 'rotate(180deg)' })),
      state('expanded', style({ transform: 'rotate(0deg)' })),
      transition('collapsed => expanded', [
        style({ backgroundColor: 'transparent' }),
        animate('0.35s ease', style({ transform: 'rotate(0deg)' }))
      ]),
      transition('expanded => collapsed', [
        style({ backgroundColor: 'transparent' }),
        animate('0.35s ease', style({ transform: 'rotate(180deg)' }))
      ]),
    ]),
  ],
})
export class AccountDetailComponent implements OnInit {
  public isCollapsed = false;
  public isSubmittingPrintAccountStatementRequest = false;
  public currentAccount: CosmosCustomer;
  public icons = {
    cart: cartIcon,
    chevronDown: chevronDownIcon,
    go: paperPlaneIcon,
    search: searchIcon,
    questionMark: questionCircleIcon,
    qrCode: qrCodeIcon
  };
  public isQrCodeDialogVisible = false;
  public generatedQrCodeData = '';

  loading = true;
  customer?: CosmosCustomer;
  customerSettings?: CustomerSettings;
  currentUserEmail?: string;
  chartSeriesColor = ChartSeries.colors;
  dashboardCards: DashboardCard[] = [];
  timestamp: number;
  warehousesLoading = true;
  warehouses: Warehouse[] = [];
  public get warehouseDropdownListData() {
    if (!this.prefferedWarehouse) {
      return this.warehouses;
    }
    else {
      // Exclude the preffered warehouse from the list because it is added as the default selected item
      return this.warehouses.filter(w => w.bkWarehouseKey !== this.prefferedWarehouse?.bkWarehouseKey);
    }
  }
  prefferedWarehouse?: Warehouse;

  isAlcro = false;
  isBeckers = false;

  private readonly standardItems = [
    {
      title: 'product.findProducts',
      icon: this.icons.search,
      action: () => this.searchForProducts(),
      type: AccountDetailsCardType.Search,
      inputPlaceholder: 'product.searchForProducts'
    },
    {
      title: 'routing.userGuide',
      icon: this.icons.questionMark,
      action: () => this.onUserGuideClick(),
      type: AccountDetailsCardType.Button,
      buttonIcon: this.icons.go
    },
    {
      title: 'routing.openOrders',
      icon: this.icons.cart,
      action: () => this.onMyCartClick(),
      type: AccountDetailsCardType.Button,
      buttonIcon: this.icons.go
    }
  ];

  private readonly qrCodeItem = {
    title: 'qrcode.QrCode',
    icon: this.icons.qrCode,
    action: () => this.openQrCodeDialog(),
    type: AccountDetailsCardType.Button,
    buttonIcon: this.icons.go
  };

  public get items(): typeof this.standardItems {
    return this.canGenerateQrCode(this.customer)
      ? [this.standardItems[0], this.qrCodeItem, ...this.standardItems.slice(1)]
      : this.standardItems;
  }

  private canGenerateQrCode(customer?: CosmosCustomer): boolean {
    const paymentTermsCode = customer?.PaymentTerms_Code?.trim().toUpperCase();
    return customer?.TikkurilaCompanyRegisterCode === CompanyCode.Norway
      && !!paymentTermsCode && paymentTermsCode !== 'COD';
  }

  constructor(
    private activeRoute: ActivatedRoute,
    private companyAdminConfigurationStore: CompanyAdminConfigurationStore,
    private customerService: ICustomerService,
    private customerSettingsService: ICustomerSettingsService,
    private customerSettingsStore: CustomerSettingsStore,
    private kendoAlertService: KendoAlertService,
    private printService: IPrintService,
    private router: Router,
    private securityService: SecurityService,
    private warehouseService: WarehouseService,
    private translateService: TranslateService,
  ) { }

  ngOnInit() {
    this.currentUserEmail = this.securityService.getUserEmail();
    this.activeRoute.params.subscribe(() => {
      this.loadAccountInformation();
      this.loadWarehouses();
    });
  }

  public labelContent = (e) => {
    return e.value;
  }

  public labelContentDonut = (e) => {
    return e.category;
  }

  private loadAccountInformation(): void {
    this.loading = true;

    this.securityService.getCurrentAccount().pipe(
      switchMap(account => {
        this.closeQrCodeDialog();
        this.loading = !!account; // If account is loaded, we are still loading the customer
        this.currentAccount = account;
        if (!account) {
          return EMPTY;
        }
        return this.customerService.getCustomerByCode(account.AXCode);
      }),
      catchError(err => {
        if (isPortalUserAccessDenied(err)) {
          throw err;
        }

        console.error('Error loading customer:', err);
        throw new Error('alerts.errorDuringDataRetrieve');
      }),
    ).subscribe({
      next: customer => {
        if (!customer) {
          this.kendoAlertService.showErrorAlert('alerts.errorDuringDataRetrieve');
        }
        if (customer) {
          this.customer = customer;
          this.setCustomerTypeFlags();
          this.companyAdminConfigurationStore.load(this.customer.TikkurilaCompanyRegisterCode)
            .pipe(take(1))
            .subscribe(this.getDashboardCards.bind(this));

          this.customerSettingsStore.load(customer.bkCustomerKey)
            .pipe(take(1))
            .subscribe(settings => {
              if (settings) {
                this.customerSettings = settings;
                this.prefferedWarehouse = this.getPrefferedWarehouse();
              }
            });
        }
        this.loading = false;
      },
      error: error => {
        this.kendoAlertService.showErrorAlert(error);
        this.loading = false;
      }
    });
  }

  private setCustomerTypeFlags() {
    if (this.customer) {
      const salesDistrict = this.customer.SalesDistrict;
      this.isAlcro = salesDistrict && salesDistrict.startsWith(SalesDistrict.AlcroSweden);
      this.isBeckers = salesDistrict && salesDistrict.startsWith(SalesDistrict.BeckersSweden);
    }
  }

  private getDashboardCards(config: CompanyAdminConfiguration): void {
    if (config) {
      this.dashboardCards = config.dashboardCards.filter(c => {
        if (!c.visibleFor || c.visibleFor === DashboardCardVisibleFor.None) {
          return false;
        }
        if (this.isAlcro && c.visibleFor === DashboardCardVisibleFor.Alcro) {
          return true;
        }
        if (this.isBeckers && c.visibleFor === DashboardCardVisibleFor.Beckers) {
          return true;
        }
        return c.visibleFor === DashboardCardVisibleFor.All;
      })
    }
  }

  private loadWarehouses(): void {
    this.warehousesLoading = true;
    this.warehouseService.get().subscribe({
      next: warehouses => {
        if (warehouses) {
          this.warehouses = warehouses;
        }
        this.prefferedWarehouse = this.getPrefferedWarehouse();
        this.warehousesLoading = false;
      },
      error: error => {
        this.kendoAlertService.showErrorAlert(error);
        this.warehousesLoading = false;
      }
    });
  }

  public getPrefferedWarehouse(): Warehouse | undefined {
    if (this.customerSettings && this.warehouses && this.warehouses.length > 1) {
      return this.warehouses.find(w => w.bkWarehouseKey === this.customerSettings.bkWarehouseKey);
    }
    else if (this.warehouses && this.warehouses.length === 1) {
      const warehouse = this.warehouses[0];
      if (!this.customerSettings || this.customerSettings.bkWarehouseKey !== warehouse.bkWarehouseKey) {
        this.selectedWarehouseChanged(warehouse).subscribe();
      }
      return warehouse;
    }
    return undefined;
  }

  public selectedWarehouseChanged(warehouse: Warehouse): Observable<void> {
    if (!warehouse || !warehouse.bkWarehouseKey) {
      return of();
    }

    this.warehousesLoading = true;

    return new Observable<void>(observer => {
      if (!this.customerSettings) {
        this.customerSettings = <CustomerSettings>{
          bkCustomerKey: this.customer?.bkCustomerKey,
          bkWarehouseKey: warehouse.bkWarehouseKey,
        };

        this.customerSettingsService.create(this.customerSettings).subscribe({
          next: () => {
            this.customerSettingsStore.reload().subscribe(() => {
              this.warehousesLoading = false;
              observer.next();
              observer.complete();
            });
          },
          error: error => {
            this.warehousesLoading = false;
            this.kendoAlertService.showErrorAlert(error);
            observer.error(error);
          }
        });
      }
      else {
        this.customerSettings.bkWarehouseKey = warehouse.bkWarehouseKey;

        this.customerSettingsService.update(this.customerSettings).subscribe({
          next: () => {
            this.customerSettingsStore.reload().subscribe(() => {
              this.warehousesLoading = false;
              observer.next();
              observer.complete();
            });
          },
          error: error => {
            this.warehousesLoading = false;
            this.kendoAlertService.showErrorAlert(error);
            observer.error(error);
          }
        });
      }
    });
  }

  public searchValue: string = '';

  public searchValueChange(value: string) {
    this.searchValue = value;
  }

  public searchForProducts() {
    if (!this.searchValue) {
      return;
    }
    if (!this.prefferedWarehouse) {
      this.showSelectWarehouseDialog = true;
      return;
    }
    this.router.navigate(['./draftorder'], { relativeTo: this.activeRoute, state: { productSearch: this.searchValue } });
  }

  public showSelectWarehouseDialog = false;
  public selectWarehouseDialogClosed(warehosue) {
    this.showSelectWarehouseDialog = false;
    this.loading = true;
    if (warehosue) {
      const subscription = this.selectedWarehouseChanged(warehosue).subscribe({
        next: () => {
          this.loading = false;
          subscription.unsubscribe();
          this.router.navigate(['./draftorder'], { relativeTo: this.activeRoute, state: { productSearch: this.searchValue } });
        },
        error: () => {
          this.loading = false;
          subscription.unsubscribe();
        }
      });
    }
  }

  public onUserGuideClick() {
    const siteUrl = window.location.href.split('/').slice(0, 3).join('/');
    this.timestamp = new Date().getTime();
    window.open(`${siteUrl}/assets/user_guide_EN.pdf?nocache=${this.timestamp}#page=1`, '_blank');
  }

  public onMyCartClick() {
    this.router.navigate(['./openorders'], { relativeTo: this.activeRoute });
  }

  public canCreatePrintAccountStatementRequest(): boolean {
    return this.currentUserEmail
      && this.customer
      && this.customer.bkCustomerKey
      && this.customer.bkCustomerKey.length > 0;
  }

  public printAccountStatement() {
    this.isSubmittingPrintAccountStatementRequest = true;

    this.printService.printAccountStatement(this.currentUserEmail, this.customer).subscribe({
      next: response => {
        if (!response.isOk) {
          this.kendoAlertService.showErrorAlert(response.errorMsg);
        }
        else {
          this.kendoAlertService.showSuccessAlert("alerts.printAccountStatementRequestSubmittedSuccessfully", true);
        }
        this.isSubmittingPrintAccountStatementRequest = false;
      },
      error: error => {
        this.kendoAlertService.showErrorAlert(error);
        this.isSubmittingPrintAccountStatementRequest = false;
      }
    });
  }

  public openQrCodeDialog(): void {
    this.closeQrCodeDialog();
    const customer = this.securityService.getCurrentAccountObject();
    if (!this.canGenerateQrCode(customer) || !customer?.AXCode || !this.securityService.hasPortalAccess()
      || !this.securityService.hasCustomerAccess(customer.AXCode)) {
      this.kendoAlertService.showErrorAlert('qrcode.noIdentifier');
      return;
    }

    const customerStatus = customer.CustomerStatusName?.trim().toLowerCase();
    const isCustomerOpen = customerStatus === 'active' || customerStatus === 'not blocked';
    const statusText = this.translateService.instant(
      isCustomerOpen ? 'qrcode.mdsStatusNotBlocked' : 'qrcode.mdsStatusBlockedForAll');

    this.generatedQrCodeData = `${customer.AXCode};${this.securityService.getUserDisplayName()};${statusText}`;
    this.isQrCodeDialogVisible = true;
  }

  public closeQrCodeDialog = (): void => {
    this.isQrCodeDialogVisible = false;
    this.generatedQrCodeData = '';
  };

}
